import { Component, computed, DestroyRef, HostListener, inject, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Services } from "src/app/services/api.service";
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { ProfileSidebarComponent } from 'src/app/layout/profile-sidebar/profile-sidebar.component';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CountriesStore } from 'src/app/services/countries.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNames } from 'src/app/common/modal.config';
import { ModalService } from 'src/app/services/modal.service';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { Country } from 'src/app/common/countries.model';
import { GetUserProfileService } from 'src/app/services/getUserProfile.service';
import { trimAllControls } from 'src/app/utils/trim-values.util';
import { strictEmailValidator } from "../../utils/validators";
import { GdprSubscriptionsService } from "../../services/gdpr-subscriptions.service";
import { delay, finalize, of, switchMap, tap } from "rxjs";

@Component({
  selector: 'app-update-profile',
  templateUrl:  `./${environment.pageFolder}/update-profile.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/update-profile.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ProfileSidebarComponent, CommonModule, TranslateModule, ReactiveFormsModule, ChangePasswordComponent]
})

export class UpdateProfileComponent implements OnInit {
  successMessage: string = "";
  userEmail: string = '';
  errorMessage: string = "";
  updateProfileTypeList = this.formBuilder.group({
    firstName: ['', [ Validators.required ]],
    lastName: ['', [ Validators.required ]],
    /*email: ['', [ Validators.required, strictEmailValidator ]],*/
    address: ['', [ Validators.required ]],
    houseNo: ['', [ Validators.required ]],
    city: ['', [ Validators.required ]],
    postNo: ['', [ Validators.required ]],
    countryId: ['', [ Validators.required ]],
    phoneNo: ['', [ Validators.required ]]
  });
  countryList: Country[] = [];
  loader = document.getElementById("loader-container");
  page = environment.page;
  private readonly destroyRef = inject(DestroyRef);

  public readonly modalNamesEnum = ModalNames;
  readonly isChangePasswordModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.ChangePassword) ?? false);

  private userId: number | null = null;
  readonly gdprSubscriptionService = inject(GdprSubscriptionsService);
  gdprForm = this.formBuilder.group({
    options: this.formBuilder.array([] as FormGroup[])
  });
  optionsArraySignal = signal<FormArray | null>(null);

  constructor(
    private readonly formBuilder: FormBuilder,
    public services: Services,
    private readonly http: HttpClient,
    private readonly countriesStore: CountriesStore, private readonly errorMessageService: ErrorMessageService,
    public modalService: ModalService,
    private readonly profileStore: GetUserProfileService
  ){
    this.countriesStore.fetchCountries().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.countryList = response;
    })
  }

  ngOnInit(): void {
    this.loader!.style.display = "none";
    this.getUserProfile();
  }

  updateUserProfile() {
    const form = this.updateProfileTypeList;
    trimAllControls(form);

    /*const emailControl = form.get('email')!;
    if (emailControl.value && emailControl.hasError('invalidEmail')) {
      emailControl.markAsTouched();
      this.errorMessage = this.errorMessageService.getErrorMessage('invalidEmail');
      return;
    }*/

    if (form.invalid) {
      form.markAllAsTouched();
      this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      return;
    }

    const updatedProfile = {
      firstName: form.value.firstName!,
      lastName: form.value.lastName!,
      /*email: form.value.email!,*/
      address: form.value.address!,
      houseNo: form.value.houseNo!,
      city: form.value.city!,
      postNo: form.value.postNo!,
      countryId: Number(form.value.countryId!),
      phoneNo: form.value.phoneNo!
    };

    this.loader!.style.display = "flex";
    this.errorMessage = "";
    this.successMessage = "";

    this.profileStore.updateUserProfile(updatedProfile).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (latestProfileData) => {
        this.loader!.style.display = "none";
        this.successMessage = this.errorMessageService.getSuccessMessage('profileUpdatedSuccess');

        this.updateProfileTypeList.patchValue({
          firstName: latestProfileData.firstName,
          lastName: latestProfileData.lastName,
          /*email: latestProfileData.email,*/
          address: latestProfileData.address,
          houseNo: latestProfileData.houseNo,
          city: latestProfileData.city,
          postNo: latestProfileData.postNo,
          countryId: latestProfileData.countryId ? latestProfileData.countryId.toString() : '',
          phoneNo: latestProfileData.phoneNo
        });

        this.profileStore.getConnectionUserProfile(true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: () => {
        this.loader!.style.display = "none";
        this.errorMessage = this.errorMessageService.getErrorMessage('error');
      },
    });
  }

  getUserProfile() {
    this.profileStore.getUserProfile().pipe(
      tap((userProfile) => {
        this.userId = userProfile.id;
        this.updateProfileTypeList.patchValue({
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          /*email: userProfile.email,*/
          address: userProfile.address,
          houseNo: userProfile.houseNo,
          city: userProfile.city,
          postNo: userProfile.postNo,
          countryId: userProfile.countryId ? userProfile.countryId.toString() : '',
          phoneNo: userProfile.phoneNo
        });
      }),
      switchMap(userProfile =>
        this.gdprSubscriptionService.getOptionalQuestionsForm(userProfile.id)
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (formGroup) => {
        this.applyGdprOptions(formGroup);
      },
      error: (error: HttpErrorResponse) => {
        if(error.status === 401) {
          this.services.logout();
        }
      },
    });
  }

  openChangePassowordModal(){
    this.modalService.openModal(ModalNames.ChangePassword);
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_: KeyboardEvent) {
    if (this.isChangePasswordModalOpen()) {
      this.modalService.closeModal(ModalNames.ChangePassword);
    }
  }

  onGdprToggle(index: number) {
    const formArray = this.optionsArraySignal();
    if (!formArray) {
      return;
    }

    const control = formArray.at(index) as FormGroup;
    const answeredControl = control.get('answered')!;
    const questionId = control.get('questionId')!.value as number;
    const answered = !!answeredControl.value;
    const previousValue = !answered; // for revert on failure
    const gdprTextId = this.gdprSubscriptionService.gdprTextId();

    if (!gdprTextId) {
      answeredControl.patchValue(previousValue, { emitEvent: false });
      return;
    }

    this.loader!.style.display = "flex";
    answeredControl.disable({ emitEvent: false });

    this.gdprSubscriptionService.patchQuestion(gdprTextId, questionId, answered)
      .pipe(
        delay(750),
        switchMap(ok => {
          const userId = this.userId;
          if (!ok || userId === null) {
            return of(null);
          }
          return this.gdprSubscriptionService.refreshOptionalQuestionsForm(userId);
        }),
        finalize(() => {
          answeredControl.enable({ emitEvent: false });
          this.loader!.style.display = 'none';
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((formGroupOrNull) => {
        if (!formGroupOrNull) {
          answeredControl.patchValue(previousValue, { emitEvent: false });
          return;
        }

        this.applyGdprOptions(formGroupOrNull);
      });
  }

  private applyGdprOptions(formGroup: FormGroup | null): void {
    const formArray = (formGroup?.get('options') as FormArray<FormGroup> | null) ?? this.formBuilder.array([] as FormGroup[]);
    this.gdprForm.setControl('options', formArray);
    this.optionsArraySignal.set(formArray);
  }
}
